/**
 * Buyer onboarding + eligibility client (hand-rolled).
 *
 * Marketplace transactional layer — buyer signup is a PUBLIC flow that mirrors
 * the farmer /signup wizard (phone → OTP → consent → PIN), but against the
 * dedicated POST /buyers/signup endpoint and the OTP purpose 'buyer_signup'.
 *
 * Orval has NOT been regenerated for /buyers yet, so these functions are
 * hand-rolled on the shared axios mutator (src/api/client.ts → fseAxios), the
 * same pattern as src/api/photos.ts / provenance.ts. They return the response
 * BODY directly (fseAxios unwraps `response.data`) and raise FseApiError on a
 * non-2xx so TanStack Query routes them to its error path.
 *
 * NOTE on OTP: the generated OTP purpose enum now includes 'buyer_signup'
 * (TD-124 regen), so BUYER_OTP_PURPOSE below is typed against it — a future
 * server-side rename breaks the build. We still hand-roll the two OTP calls
 * (rather than use the generated client) so the buyer purpose is baked in at one
 * place; the wire contract is identical to the farmer flow.
 *
 * KYC: the buyer FICA submit/read reuses the EXISTING generated /kyc client
 * (kycControllerSubmit / kycControllerGetMe) — re-exported here so the buyer
 * verification screen has a single import surface. Buyers and farmers share the
 * same /kyc module (FICA tier-1 intent), so no new client is needed.
 */
import { fseAxios } from './client';
import {
  buyersControllerGetLocation,
  buyersControllerSetLocation,
} from './generated/buyers/buyers';
import { kycControllerGetMe, kycControllerSubmit } from './generated/kyc/kyc';
import type {
  AuthTokens,
  BuyerEligibilityDto,
  BuyerLocationDto,
  BuyerLocationGpsDto,
  BuyerSignupDto,
  BuyerSummaryDto,
  BuyerSignupDtoPreferredLang,
  KycDto,
  OtpVerifyRequestPurpose,
} from './generated/models';

// DTOs are the single source of truth in the generated client (TD-124 regen),
// re-exported under the names the screens use; the client functions stay
// hand-rolled because they return the unwrapped body (fseAxios) screens consume.

/** Supported preferred-language codes (matches the farmer signup surface). */
export type BuyerPreferredLang = BuyerSignupDtoPreferredLang;
export type AuthTokensResponse = AuthTokens;
export type BuyerSignupBody = BuyerSignupDto;
export type BuyerEligibility = BuyerEligibilityDto;
export type BuyerSummary = BuyerSummaryDto;

/**
 * The OTP purpose for buyer onboarding. Typed against the generated enum so a
 * server-side rename of the purpose breaks the build (TD-124).
 */
const BUYER_OTP_PURPOSE: OtpVerifyRequestPurpose = 'buyer_signup';

/**
 * Request an OTP for the buyer-signup flow. PUBLIC — the caller has no token
 * yet. The response shape mirrors the farmer OTP request (an opaque ack); we
 * don't read the body, only that it resolved 2xx.
 */
export function requestBuyerSignupOtp(phone: string, signal?: AbortSignal): Promise<unknown> {
  return fseAxios<unknown>({
    url: '/auth/otp/request',
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: { phone, purpose: BUYER_OTP_PURPOSE },
    signal,
  });
}

/**
 * Verify the OTP code for the buyer-signup flow. PUBLIC. A non-2xx (e.g. 401
 * bad/expired code) raises FseApiError, which the screen maps to friendly copy.
 */
export function verifyBuyerSignupOtp(
  phone: string,
  code: string,
  signal?: AbortSignal,
): Promise<unknown> {
  return fseAxios<unknown>({
    url: '/auth/otp/verify',
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: { phone, code, purpose: BUYER_OTP_PURPOSE },
    signal,
  });
}

/**
 * Complete buyer registration. PUBLIC — precondition is a verified
 * 'buyer_signup' OTP for this phone + an accepted consent version. On success
 * the server mints an access/refresh pair (same shape login returns) plus the
 * new user id + role; the screen persists the tokens and routes by role.
 */
export function buyerSignup(body: BuyerSignupBody, signal?: AbortSignal): Promise<AuthTokensResponse> {
  return fseAxios<AuthTokensResponse>({
    url: '/buyers/signup',
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: body,
    signal,
  });
}

/**
 * Read the signed-in buyer's FICA eligibility. Authed (bearer injected by the
 * mutator). Drives the "you can / cannot place offers yet" gate.
 */
export function getBuyerEligibility(signal?: AbortSignal): Promise<BuyerEligibility> {
  return fseAxios<BuyerEligibility>({
    url: '/buyers/me/eligibility',
    method: 'GET',
    signal,
  });
}

/**
 * Read the signed-in buyer's account summary (orders aggregate). Authed (bearer
 * injected by the mutator). Powers the buyer Account hub stat-grid.
 */
export function getBuyerSummary(signal?: AbortSignal): Promise<BuyerSummary> {
  return fseAxios<BuyerSummary>({
    url: '/buyers/me/summary',
    method: 'GET',
    signal,
  });
}

/**
 * Submit a FICA tier-1 verification intent (buyer). Thin wrapper over the
 * existing generated /kyc client so the buyer verification screen imports a
 * single module. Returns the updated KYC state.
 */
export function submitBuyerKyc(): Promise<KycDto> {
  return kycControllerSubmit({ ficaTier: 1 });
}

/** Read the signed-in buyer's own KYC state. Re-export of the generated client. */
export function getMyKyc(signal?: AbortSignal): Promise<KycDto> {
  return kycControllerGetMe(signal);
}

// --------------------------------------------------------------------------
// Buyer location (geo-distance "near me")
// --------------------------------------------------------------------------
//
// The buyer's precise point is stored server-side (same fidelity as a farm) and
// used only to derive coarse distance bands on listings — it is NEVER shown to a
// seller. These wrap the generated /buyers/me/location client (the buyer reads
// their OWN point for the capture UI).

/** Read the signed-in buyer's own stored location ({lat,lng} | null). */
export function getMyBuyerLocation(signal?: AbortSignal): Promise<BuyerLocationDto> {
  return buyersControllerGetLocation(signal);
}

/** Set (or clear, with null) the buyer's location. */
export function setMyBuyerLocation(
  location: BuyerLocationGpsDto | null,
): Promise<BuyerLocationDto> {
  return buyersControllerSetLocation({ location });
}

export type { KycDto, BuyerLocationDto, BuyerLocationGpsDto };
